import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Progress,
  Popconfirm,
  Radio,
  Row,
  Select,
  Space,
  Tabs,
  Upload,
  Table,
  Tag,
  Tooltip,
  Typography,
  message
} from 'antd';
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  LinkOutlined,
  PictureOutlined,
  PlusOutlined,
  ReloadOutlined,
  StarFilled,
  StarOutlined,
  SyncOutlined
} from '@ant-design/icons';
import { useLanguage } from '../contexts/LanguageContext';
import { commerceServerTablePagination, COMMERCE_DEFAULT_PAGE_SIZE } from '../constants/commerceListPagination';
import { getUserPrimaryCompanyId, hasRole } from '../utils/permissionUtils';
import {
  COMMERCE_DESCRIPTION_LOCALE_EXTRA_KEYS,
  COMMERCE_NAME_LOCALE_EXTRA_KEYS,
  COMMERCE_UNIT_OF_MEASURE_LOCALE_EXTRA_KEYS,
  COMMERCE_UNIT_OF_BROKEN_PACK_LOCALE_EXTRA_KEYS,
  parseNameLocalesField,
  resolveLocalizedEntityName
} from '../constants/commerceNameLocales';
import { buildCommerceAuditColumns, useCommerceUserTimezone } from '../utils/commerceListDateUtils';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

/** 與後端 CommerceProductPriceResolver 一致：UTC；價格1 優先於價格2 */
function resolveEffectiveCatalogPricePreview(formVals, utcMoment) {
  if (!formVals) return null;
  const p = formVals;
  const hasT1 = p.price1 != null && p.price1 !== '' && p.validFrom1 && p.validTo1;
  const hasT2 = p.price2 != null && p.price2 !== '' && p.validFrom2 && p.validTo2;
  if (!hasT1 && !hasT2) {
    const leg = p.price;
    if (leg == null || leg === '') return null;
    const n = Number(leg);
    return Number.isFinite(n) ? n : null;
  }
  const u = utcMoment && dayjs.isDayjs(utcMoment) ? utcMoment : dayjs.utc(utcMoment);
  const uv = u.valueOf();
  let hit1;
  if (hasT1) {
    const f = dayjs.utc(p.validFrom1);
    const t = dayjs.utc(p.validTo1);
    if (uv >= f.valueOf() && uv <= t.valueOf()) hit1 = Number(p.price1);
  }
  if (hit1 != null && Number.isFinite(hit1)) return hit1;
  let hit2;
  if (hasT2) {
    const f = dayjs.utc(p.validFrom2);
    const t = dayjs.utc(p.validTo2);
    if (uv >= f.valueOf() && uv <= t.valueOf()) hit2 = Number(p.price2);
  }
  if (hit2 != null && Number.isFinite(hit2)) return hit2;
  return null;
}

const { Title, Text } = Typography;
const { Dragger } = Upload;
const AVAILABILITY_OPTIONS = [
  { label: 'in stock', value: 'in stock' },
  { label: 'out of stock', value: 'out of stock' }
];
const CONDITION_OPTIONS = [
  { label: 'new', value: 'new' },
  { label: 'used', value: 'used' },
  { label: 'refurbished', value: 'refurbished' }
];
const STATUS_OPTIONS = [
  { label: 'active', value: 'active' },
  { label: 'inactive', value: 'inactive' }
];
const GENDER_OPTIONS = [
  { label: 'male', value: 'male' },
  { label: 'female', value: 'female' },
  { label: 'unisex', value: 'unisex' }
];

const CommerceProductsPage = () => {
  const { t, currentLanguage } = useLanguage();
  const userTimezoneOffset = useCommerceUserTimezone();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [products, setProducts] = useState([]);
  const [keyword, setKeyword] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [collectionFilterIds, setCollectionFilterIds] = useState([]);
  const [collectionsForFilter, setCollectionsForFilter] = useState([]);
  const [collectionsForFilterLoading, setCollectionsForFilterLoading] = useState(false);
  const [companyFilter, setCompanyFilter] = useState(() => getUserPrimaryCompanyId());
  const [companiesForSelect, setCompaniesForSelect] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [mediaModalOpen, setMediaModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productMedia, setProductMedia] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaPreparing, setMediaPreparing] = useState(false);
  const [draggingMediaId, setDraggingMediaId] = useState(null);
  const [altDrafts, setAltDrafts] = useState({});
  const [uploadTasks, setUploadTasks] = useState([]);
  const [inlineEditingId, setInlineEditingId] = useState(null);
  const userInfo = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('userInfo') || '{}') : {};
  const isTenantAdmin = hasRole(userInfo, 'Tenant_Admin');
  const [inlineDraft, setInlineDraft] = useState({});
  const [fbCategoryOptions, setFbCategoryOptions] = useState([]);
  const [taxonomyLoading, setTaxonomyLoading] = useState(false);
  const [catalogOptions, setCatalogOptions] = useState([]);
  const [catalogOptionsLoading, setCatalogOptionsLoading] = useState(false);
  const [form] = Form.useForm();
  const [productFormTab, setProductFormTab] = useState('basic');
  const [serverUtc, setServerUtc] = useState(null);
  const wPrice = Form.useWatch('price', form);
  const wPrice1 = Form.useWatch('price1', form);
  const wPrice2 = Form.useWatch('price2', form);
  const wVf1 = Form.useWatch('validFrom1', form);
  const wVt1 = Form.useWatch('validTo1', form);
  const wVf2 = Form.useWatch('validFrom2', form);
  const wVt2 = Form.useWatch('validTo2', form);
  const drawerCurrency = Form.useWatch('currency', form);
  const drawerPreviewEffective = useMemo(
    () => resolveEffectiveCatalogPricePreview(
      {
        price: wPrice,
        price1: wPrice1,
        price2: wPrice2,
        validFrom1: wVf1,
        validTo1: wVt1,
        validFrom2: wVf2,
        validTo2: wVt2
      },
      serverUtc || dayjs.utc()
    ),
    [wPrice, wPrice1, wPrice2, wVf1, wVt1, wVf2, wVt2, serverUtc]
  );
  const drawerPrimaryImageUrl = useMemo(() => {
    const primaryMedia = productMedia.find(x => x.mediaType === 'image' && x.isPrimary)
      || productMedia.find(x => x.mediaType === 'image');
    return primaryMedia?.url || primaryMedia?.Url || editingRecord?.imageUrl || selectedProduct?.imageUrl || '';
  }, [productMedia, editingRecord, selectedProduct]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const selectedProductByIdRef = useRef(new Map());
  const [productsTotal, setProductsTotal] = useState(0);
  const [listPage, setListPage] = useState(1);
  const [listPageSize, setListPageSize] = useState(COMMERCE_DEFAULT_PAGE_SIZE);
  const [addToCollectionModalOpen, setAddToCollectionModalOpen] = useState(false);
  const [collectionMode, setCollectionMode] = useState('existing');
  const [collectionsForAdd, setCollectionsForAdd] = useState([]);
  const [collectionsForAddLoading, setCollectionsForAddLoading] = useState(false);
  const [selectedCollectionIdsForAdd, setSelectedCollectionIdsForAdd] = useState([]);
  const [addToCollectionSearchKeyword, setAddToCollectionSearchKeyword] = useState('');
  const [newCollectionName, setNewCollectionName] = useState('');
  const [newCollectionDescription, setNewCollectionDescription] = useState('');
  const [savingAddToCollection, setSavingAddToCollection] = useState(false);
  const [productCollectionIds, setProductCollectionIds] = useState([]);
  const [productCollectionIdsOriginal, setProductCollectionIdsOriginal] = useState([]);
  const [collectionsForDrawer, setCollectionsForDrawer] = useState([]);
  const [collectionsForDrawerLoading, setCollectionsForDrawerLoading] = useState(false);

  const fetchWithAuth = useCallback((url, options = {}) => {
    const token = localStorage.getItem('token');
    const headers = { ...(options.headers || {}) };
    const isFormData = options.body instanceof FormData;
    if (!isFormData && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
    if (token) headers.Authorization = `Bearer ${token}`;
    headers['X-UI-Locale'] = currentLanguage || 'zh-TC';
    return fetch(url, { ...options, headers });
  }, [currentLanguage]);

  const extractResponseError = async (response) => {
    const text = await response.text();
    if (!text) return `HTTP ${response.status}`;
    try {
      const data = JSON.parse(text);
      return data?.error || data?.detail || data?.message || text;
    } catch {
      return text;
    }
  };

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (keyword.trim()) params.set('search', keyword.trim());
      if (companyFilter) params.set('companyId', companyFilter);
      collectionFilterIds.forEach((id) => params.append('collectionIds', id));
      params.set('page', String(listPage));
      params.set('pageSize', String(listPageSize));
      const response = await fetchWithAuth(`/api/commerce/products?${params.toString()}`);
      if (!response.ok) throw new Error(await extractResponseError(response));
      const data = await response.json();
      const items = Array.isArray(data?.items) ? data.items : [];
      const total = typeof data?.total === 'number' ? data.total : items.length;
      setProducts(items);
      setProductsTotal(total);
    } catch (err) {
      message.error(`${t('commerceProducts.loadFailed')}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, keyword, companyFilter, collectionFilterIds, listPage, listPageSize, t]);

  const clearProductSelection = useCallback(() => {
    setSelectedRowKeys([]);
    selectedProductByIdRef.current = new Map();
  }, []);

  const onProductTableSelectionChange = useCallback(
    (keys) => {
      setSelectedRowKeys(keys);
      const map = selectedProductByIdRef.current;
      keys.forEach((id) => {
        const row = products.find((p) => p.id === id);
        if (row) map.set(id, row);
      });
      for (const id of [...map.keys()]) {
        if (!keys.includes(id)) map.delete(id);
      }
    },
    [products]
  );

  const loadCompaniesForSelect = useCallback(async () => {
    try {
      const res = await fetchWithAuth('/api/commerce/companies-for-select');
      if (!res.ok) return;
      const data = await res.json();
      setCompaniesForSelect(Array.isArray(data) ? data : []);
    } catch (err) {
      console.warn('載入公司列表失敗:', err);
    }
  }, [fetchWithAuth]);

  const loadTaxonomyOptions = useCallback(async () => {
    setTaxonomyLoading(true);
    try {
      const fbResp = await fetchWithAuth('/api/commerce/taxonomy/facebook?locale=en_US&limit=500');
      if (!fbResp.ok) throw new Error(await fbResp.text());
      const fbData = await fbResp.json();
      setFbCategoryOptions((fbData?.data || []).map(x => ({ label: x.label, value: x.value })));
    } catch (err) {
      message.warning(`${t('commerceProducts.loadFailed')}: ${err.message}`);
      setFbCategoryOptions([]);
    } finally {
      setTaxonomyLoading(false);
    }
  }, [fetchWithAuth]);

  const loadCollectionsForFilter = useCallback(async () => {
    setCollectionsForFilterLoading(true);
    try {
      const params = new URLSearchParams();
      if (companyFilter) params.set('companyId', companyFilter);
      const all = [];
      let page = 1;
      const pageSize = 100;
      for (;;) {
        const q = new URLSearchParams(params);
        q.set('page', String(page));
        q.set('pageSize', String(pageSize));
        const res = await fetchWithAuth(`/api/commerce/collections?${q.toString()}`);
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        const items = Array.isArray(data?.items) ? data.items : [];
        all.push(...items);
        const total = typeof data?.total === 'number' ? data.total : items.length;
        if (all.length >= total || items.length === 0) break;
        page += 1;
      }
      setCollectionsForFilter(all);
    } catch (err) {
      console.warn('載入集合篩選列表失敗:', err);
      setCollectionsForFilter([]);
    } finally {
      setCollectionsForFilterLoading(false);
    }
  }, [fetchWithAuth, companyFilter]);

  const syncTaxonomy = async (provider) => {
    try {
      setTaxonomyLoading(true);
      const response = await fetchWithAuth('/api/commerce/taxonomy/sync', {
        method: 'POST',
        body: JSON.stringify({ provider, locale: 'en_US' })
      });
      if (!response.ok) throw new Error(await response.text());
      const result = await response.json();
      message.success(t('commerceProducts.taxonomySyncSuccess', { provider, count: result.count || 0 }));
      await loadTaxonomyOptions();
    } catch (err) {
      message.error(`${t('commerceProducts.taxonomySyncFailed', { provider })}: ${err.message}`);
    } finally {
      setTaxonomyLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    loadTaxonomyOptions();
  }, [loadTaxonomyOptions]);

  useEffect(() => {
    loadCompaniesForSelect();
  }, [loadCompaniesForSelect]);

  useEffect(() => {
    loadCollectionsForFilter();
  }, [loadCollectionsForFilter]);

  const collectionFilterOptions = useMemo(
    () => collectionsForFilter.map((c) => ({
      label: resolveLocalizedEntityName(c.name, c.nameLocales ?? c.name_locales, currentLanguage) || c.name || c.id,
      value: c.id
    })),
    [collectionsForFilter, currentLanguage]
  );

  useEffect(() => {
    if (!modalOpen) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchWithAuth('/api/commerce/server-time');
        if (!r.ok) throw new Error('server-time');
        const j = await r.json();
        if (!cancelled) setServerUtc(j.utcNow ? dayjs.utc(j.utcNow) : dayjs.utc());
      } catch {
        if (!cancelled) setServerUtc(dayjs.utc());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modalOpen, fetchWithAuth]);

  const loadCatalogOptions = useCallback(async () => {
    setCatalogOptionsLoading(true);
    try {
      const res = await fetchWithAuth('/api/commerce/catalogs');
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setCatalogOptions(list.map((c) => ({ label: c.name || c.id, value: c.id })));
    } catch (err) {
      console.warn(t('commerceProducts.loadCatalogFailed'), err);
      setCatalogOptions([]);
    } finally {
      setCatalogOptionsLoading(false);
    }
  }, [fetchWithAuth]);

  const openCreate = () => {
    setEditingRecord(null);
    setSelectedProduct(null);
    setProductMedia([]);
    setAltDrafts({});
    setUploadTasks([]);
    setProductFormTab('basic');
    setProductCollectionIds([]);
    setProductCollectionIdsOriginal([]);
    form.resetFields();
    form.setFieldsValue({
      companyId: isTenantAdmin ? userInfo.company_id : undefined,
      nameLocales: { 'zh-TC': '', 'zh-SC': '' },
      descriptionLocales: { 'zh-TC': '', 'zh-SC': '' },
      unitOfMeasureLocales: { 'zh-TC': '', 'zh-SC': '' },
      unitOfBrokenPack: '',
      unitOfBrokenPackLocales: { 'zh-TC': '', 'zh-SC': '' },
      qtyPerUnit: null,
      oqm: '',
      productionCategory: undefined,
      status: 'active',
      availability: 'in stock',
      condition: 'new',
      currency: 'HKD'
    });
    loadCatalogOptions();
    const cid = isTenantAdmin ? userInfo.company_id : companyFilter;
    loadCollectionsForDrawer(cid);
    setModalOpen(true);
  };

  // WABA 僅能連結一個目錄，新增時表單預設使用已連結目錄
  useEffect(() => {
    if (modalOpen && !editingRecord && catalogOptions.length === 1) {
      form.setFieldsValue({ metaCatalogId: catalogOptions[0].value });
    }
  }, [modalOpen, editingRecord, catalogOptions, form]);

  const loadCollectionsForAdd = useCallback(async () => {
    setCollectionsForAddLoading(true);
    try {
      const params = new URLSearchParams();
      if (companyFilter) params.set('companyId', companyFilter);
      const basePath = '/api/commerce/collections';
      const all = [];
      let page = 1;
      const pageSize = 100;
      for (;;) {
        const q = new URLSearchParams(params);
        q.set('page', String(page));
        q.set('pageSize', String(pageSize));
        const res = await fetchWithAuth(`${basePath}?${q.toString()}`);
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        const items = Array.isArray(data?.items) ? data.items : [];
        all.push(...items);
        const total = typeof data?.total === 'number' ? data.total : items.length;
        if (all.length >= total || items.length === 0) break;
        page += 1;
      }
      setCollectionsForAdd(all);
    } catch (err) {
      message.error(t('commerceProducts.loadCollectionsFailed'));
      setCollectionsForAdd([]);
    } finally {
      setCollectionsForAddLoading(false);
    }
  }, [fetchWithAuth, companyFilter, t]);

  const loadCollectionsForDrawer = useCallback(async (cid) => {
    setCollectionsForDrawerLoading(true);
    try {
      const params = new URLSearchParams();
      if (cid) params.set('companyId', cid);
      const all = [];
      let page = 1;
      const pageSize = 100;
      for (;;) {
        const q = new URLSearchParams(params);
        q.set('page', String(page));
        q.set('pageSize', String(pageSize));
        const res = await fetchWithAuth(`/api/commerce/collections?${q.toString()}`);
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        const items = Array.isArray(data?.items) ? data.items : [];
        all.push(...items);
        const total = typeof data?.total === 'number' ? data.total : items.length;
        if (all.length >= total || items.length === 0) break;
        page += 1;
      }
      setCollectionsForDrawer(all);
    } catch (err) {
      console.warn('載入集合列表失敗:', err);
      setCollectionsForDrawer([]);
    } finally {
      setCollectionsForDrawerLoading(false);
    }
  }, [fetchWithAuth]);

  const loadProductCollections = useCallback(async (productId) => {
    try {
      const res = await fetchWithAuth(`/api/commerce/products/${productId}/collections`);
      if (!res.ok) {
        setProductCollectionIds([]);
        setProductCollectionIdsOriginal([]);
        return;
      }
      const data = await res.json();
      const ids = (Array.isArray(data) ? data : []).map((c) => c.id ?? c.Id).filter(Boolean);
      setProductCollectionIds(ids);
      setProductCollectionIdsOriginal(ids);
    } catch {
      setProductCollectionIds([]);
      setProductCollectionIdsOriginal([]);
    }
  }, [fetchWithAuth]);

  const saveProductCollections = useCallback(async (productId) => {
    const added = productCollectionIds.filter((id) => !productCollectionIdsOriginal.includes(id));
    const removed = productCollectionIdsOriginal.filter((id) => !productCollectionIds.includes(id));
    if (added.length === 0 && removed.length === 0) return;
    await Promise.all([
      ...added.map(async (colId) => {
        const itemsRes = await fetchWithAuth(`/api/commerce/collections/${colId}/items`);
        if (!itemsRes.ok) throw new Error(await extractResponseError(itemsRes));
        const currentItems = await itemsRes.json();
        const currentIds = (Array.isArray(currentItems) ? currentItems : []).map((p) => p.id);
        const mergedIds = [...new Set([...currentIds, productId])];
        const setRes = await fetchWithAuth(`/api/commerce/collections/${colId}/items:bulk-set`, {
          method: 'POST',
          body: JSON.stringify({ productIds: mergedIds })
        });
        if (!setRes.ok) throw new Error(await extractResponseError(setRes));
      }),
      ...removed.map(async (colId) => {
        const itemsRes = await fetchWithAuth(`/api/commerce/collections/${colId}/items`);
        if (!itemsRes.ok) throw new Error(await extractResponseError(itemsRes));
        const currentItems = await itemsRes.json();
        const currentIds = (Array.isArray(currentItems) ? currentItems : []).map((p) => p.id);
        const newIds = currentIds.filter((id) => id !== productId);
        const setRes = await fetchWithAuth(`/api/commerce/collections/${colId}/items:bulk-set`, {
          method: 'POST',
          body: JSON.stringify({ productIds: newIds })
        });
        if (!setRes.ok) throw new Error(await extractResponseError(setRes));
      })
    ]);
    setProductCollectionIdsOriginal([...productCollectionIds]);
  }, [fetchWithAuth, productCollectionIds, productCollectionIdsOriginal]);

  const openAddToCollectionModal = () => {
    setCollectionMode('existing');
    setSelectedCollectionIdsForAdd([]);
    setAddToCollectionSearchKeyword('');
    setNewCollectionName('');
    setNewCollectionDescription('');
    loadCollectionsForAdd();
    setAddToCollectionModalOpen(true);
  };

  const saveAddToCollection = async () => {
    const productIds = selectedRowKeys;
    if (!productIds || productIds.length === 0) {
      message.warning(t('commerceProducts.selectAtLeastOne'));
      return;
    }
    setSavingAddToCollection(true);
    try {
      const selectedProducts = productIds
        .map((id) => selectedProductByIdRef.current.get(id))
        .filter(Boolean);
      if (selectedProducts.length !== productIds.length) {
        message.error(t('commerceProducts.selectAtLeastOne'));
        setSavingAddToCollection(false);
        return;
      }
      const phoneUniq = [...new Set(selectedProducts.map((p) => (p.phoneNumberId || '').trim()))];
      const wabaMixed = phoneUniq.length > 1;
      if (wabaMixed) {
        message.error(t('commerceProducts.addToCollectionMixedWaba'));
        setSavingAddToCollection(false);
        return;
      }
      const refPhone = phoneUniq[0] || '';

      if (collectionMode === 'new') {
        const name = (newCollectionName || '').trim();
        if (!name) {
          message.error(t('commerceProducts.newCollectionNameRequired'));
          setSavingAddToCollection(false);
          return;
        }
        const createPayload = { name, description: (newCollectionDescription || '').trim() };
        if (isTenantAdmin) createPayload.companyId = companyFilter || userInfo.company_id;
        if (refPhone) createPayload.phoneNumberId = refPhone;
        const createRes = await fetchWithAuth('/api/commerce/collections', { method: 'POST', body: JSON.stringify(createPayload) });
        if (!createRes.ok) throw new Error(await extractResponseError(createRes));
        const created = await createRes.json();
        const collectionId = created.id;
        const setRes = await fetchWithAuth(`/api/commerce/collections/${collectionId}/items:bulk-set`, {
          method: 'POST',
          body: JSON.stringify({ productIds })
        });
        if (!setRes.ok) throw new Error(await extractResponseError(setRes));
        message.success(t('commerceProducts.addToCollectionSuccess'));
        setAddToCollectionModalOpen(false);
        clearProductSelection();
      } else {
        if (!selectedCollectionIdsForAdd.length) {
          message.error(t('commerceProducts.selectCollectionsFirst'));
          setSavingAddToCollection(false);
          return;
        }
        await Promise.all(
          selectedCollectionIdsForAdd.map(async (colId) => {
            const itemsRes = await fetchWithAuth(`/api/commerce/collections/${colId}/items`);
            if (!itemsRes.ok) throw new Error(await extractResponseError(itemsRes));
            const currentItems = await itemsRes.json();
            const currentIds = (Array.isArray(currentItems) ? currentItems : []).map((p) => p.id);
            const mergedIds = [...new Set([...currentIds, ...productIds])];
            const setRes = await fetchWithAuth(`/api/commerce/collections/${colId}/items:bulk-set`, {
              method: 'POST',
              body: JSON.stringify({ productIds: mergedIds })
            });
            if (!setRes.ok) throw new Error(await extractResponseError(setRes));
          })
        );
        message.success(t('commerceProducts.addToCollectionSuccess'));
        setAddToCollectionModalOpen(false);
        clearProductSelection();
      }
    } catch (err) {
      message.error(`${t('commerceProducts.addToCollectionFailed')}: ${err.message}`);
    } finally {
      setSavingAddToCollection(false);
    }
  };

  const openEdit = (record) => {
    setEditingRecord(record);
    setSelectedProduct(null);
    setProductMedia([]);
    setAltDrafts({});
    setUploadTasks([]);
    setProductFormTab('basic');
    const nl = parseNameLocalesField(record.nameLocales);
    const dl = parseNameLocalesField(record.descriptionLocales ?? record.description_locales);
    const ul = parseNameLocalesField(record.unitOfMeasureLocales ?? record.unit_of_measure_locales);
    const bpl = parseNameLocalesField(record.unitOfBrokenPackLocales ?? record.unit_of_brokenpack_locales);
    form.setFieldsValue({
      companyId: record.companyId,
      name: record.name,
      nameLocales: {
        'zh-TC': nl['zh-TC'] || '',
        'zh-SC': nl['zh-SC'] || ''
      },
      description: record.description,
      descriptionLocales: {
        'zh-TC': dl['zh-TC'] || '',
        'zh-SC': dl['zh-SC'] || ''
      },
      unitOfMeasureLocales: {
        'zh-TC': ul['zh-TC'] || '',
        'zh-SC': ul['zh-SC'] || ''
      },
      unitOfBrokenPack: record.unitOfBrokenPack || '',
      unitOfBrokenPackLocales: {
        'zh-TC': bpl['zh-TC'] || '',
        'zh-SC': bpl['zh-SC'] || ''
      },
      productRetailerId: record.productRetailerId,
      metaProductId: record.metaProductId,
      metaCatalogId: record.metaCatalogId,
      customLabel0: record.customLabel0,
      linkUrl: record.linkUrl,
      imageUrl: record.imageUrl,
      brand: record.brand,
      productType: record.productType,
      fbProductCategory: record.fbProductCategory,
      availability: record.availability || 'in stock',
      unitOfMeasure: record.unitOfMeasure || '',
      condition: record.condition || 'new',
      currency: record.currency || 'HKD',
      color: record.color,
      size: record.size,
      gender: record.gender,
      material: record.material,
      pattern: record.pattern,
      price: record.price,
      price1: record.price1,
      validFrom1: record.validFrom1 ? dayjs.utc(record.validFrom1) : null,
      validTo1: record.validTo1 ? dayjs.utc(record.validTo1) : null,
      price2: record.price2,
      validFrom2: record.validFrom2 ? dayjs.utc(record.validFrom2) : null,
      validTo2: record.validTo2 ? dayjs.utc(record.validTo2) : null,
      status: record.status || 'active',
      minOrderQuantity: record.minOrderQuantity ?? record.min_order_quantity ?? null,
      qtyPerUnit: record.qtyPerUnit ?? record.qty_per_unit ?? null,
      oqm: record.oqm ?? '',
      productionCategory: record.productionCategory || undefined
    });
    loadCatalogOptions();
    setProductCollectionIds([]);
    setProductCollectionIdsOriginal([]);
    loadCollectionsForDrawer(record.companyId || companyFilter);
    if (record.id) loadProductCollections(record.id);
    setModalOpen(true);
  };

  const buildProductPayload = (values) => {
    const payload = { ...values };
    delete payload.nameLocales;
    delete payload.descriptionLocales;
    delete payload.unitOfMeasureLocales;
    delete payload.unitOfBrokenPackLocales;
    const nl = values.nameLocales || {};
    payload.nameLocales = {
      'zh-TC': nl['zh-TC'] ?? '',
      'zh-SC': nl['zh-SC'] ?? ''
    };
    const dl = values.descriptionLocales || {};
    payload.descriptionLocales = {
      'zh-TC': dl['zh-TC'] ?? '',
      'zh-SC': dl['zh-SC'] ?? ''
    };
    const ul = values.unitOfMeasureLocales || {};
    payload.unitOfMeasureLocales = {
      'zh-TC': ul['zh-TC'] ?? '',
      'zh-SC': ul['zh-SC'] ?? ''
    };
    const bpl = values.unitOfBrokenPackLocales || {};
    payload.unitOfBrokenPackLocales = {
      'zh-TC': bpl['zh-TC'] ?? '',
      'zh-SC': bpl['zh-SC'] ?? ''
    };
    const toIso = (d) => {
      if (d == null) return null;
      if (typeof d.toISOString === 'function') return d.toISOString();
      return dayjs.utc(d).toISOString();
    };
    payload.validFrom1 = toIso(values.validFrom1);
    payload.validTo1 = toIso(values.validTo1);
    payload.validFrom2 = toIso(values.validFrom2);
    payload.validTo2 = toIso(values.validTo2);
    const moqRaw = values.minOrderQuantity;
    const moqN = Number(moqRaw);
    payload.minOrderQuantity = moqRaw != null && moqRaw !== '' && Number.isFinite(moqN) && moqN > 0
      ? Math.round(moqN * 10000) / 10000
      : null;
    const qpuRaw = values.qtyPerUnit;
    const qpuN = Number(qpuRaw);
    payload.qtyPerUnit = qpuRaw != null && qpuRaw !== '' && Number.isFinite(qpuN) && qpuN > 0
      ? Math.round(qpuN * 10000) / 10000
      : null;
    if (!isTenantAdmin) delete payload.companyId;
    return payload;
  };

  const validateProductFormValues = async () => {
    await form.validateFields();
    return form.getFieldsValue(true);
  };

  const saveProduct = async () => {
    try {
      const values = await validateProductFormValues();
      const payload = buildProductPayload(values);
      setSaving(true);
      const isEdit = !!editingRecord;
      const url = isEdit ? `/api/commerce/products/${editingRecord.id}` : '/api/commerce/products';
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetchWithAuth(url, {
        method,
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error(await extractResponseError(response));
      const savedData = await response.json().catch(() => null);
      const savedProductId = isEdit ? editingRecord.id : (savedData?.id ?? null);
      message.success(isEdit ? t('commerceProducts.saved') : t('commerceProducts.created'));
      if (savedProductId) {
        await saveProductCollections(savedProductId);
      }
      setModalOpen(false);
      loadProducts();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(`${t('commerceProducts.saveFailed')}: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const deleteProduct = async (id) => {
    try {
      const response = await fetchWithAuth(`/api/commerce/products/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error(await extractResponseError(response));
      message.success(t('commerceProducts.deleted'));
      loadProducts();
    } catch (err) {
      message.error(`${t('commerceProducts.deleteFailed')}: ${err.message}`);
    }
  };

  const syncProduct = async (record) => {
    try {
      setSyncingId(record.id);
      const response = await fetchWithAuth(`/api/commerce/sync/product/${record.id}`, { method: 'POST' });
      if (!response.ok) throw new Error(await extractResponseError(response));
      message.success(t('commerceProducts.uploadQueued'));
    } catch (err) {
      message.error(`${t('commerceProducts.uploadFailed')}: ${err.message}`);
    } finally {
      setSyncingId(null);
    }
  };

  const batchSyncProducts = async () => {
    const ids = selectedRowKeys;
    if (!ids || ids.length === 0) {
      message.warning(t('commerceProducts.selectAtLeastOne'));
      return;
    }
    try {
      let ok = 0;
      for (const id of ids) {
        const res = await fetchWithAuth(`/api/commerce/sync/product/${id}`, { method: 'POST' });
        if (res.ok) ok += 1;
      }
      message.success(t('commerceProducts.batchUploadQueued', { count: ok, total: ids.length }));
      clearProductSelection();
    } catch (err) {
      message.error(`${t('commerceProducts.batchUploadFailed')}: ${err.message}`);
    }
  };

  const copyProduct = async (record) => {
    const id = record?.id ?? record?.Id;
    if (!id) {
      message.error(t('commerceProducts.copyFailed') + ': 無商品 ID');
      return;
    }
    try {
      const response = await fetchWithAuth('/api/commerce/products/copy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId: id })
      });
      const errText = await extractResponseError(response);
      if (!response.ok) throw new Error(errText);
      message.success(t('commerceProducts.copySuccess'));
      loadProducts();
    } catch (err) {
      message.error(`${t('commerceProducts.copyFailed')}: ${err.message}`);
    }
  };

  const batchCopyProducts = async () => {
    const ids = selectedRowKeys;
    if (!ids || ids.length === 0) {
      message.warning(t('commerceProducts.selectAtLeastOne'));
      return;
    }
    try {
      let ok = 0;
      const errors = [];
      for (const id of ids) {
        const res = await fetchWithAuth('/api/commerce/products/copy', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ productId: id })
        });
        if (res.ok) {
          ok += 1;
        } else {
          const msg = await extractResponseError(res);
          errors.push(msg);
        }
      }
      if (ok > 0) {
        message.success(t('commerceProducts.batchCopySuccess', { count: ok, total: ids.length }));
        clearProductSelection();
        loadProducts();
      }
      if (errors.length > 0) {
        message.error(`${t('commerceProducts.batchCopyFailed')}: ${errors[0]}${errors.length > 1 ? ` (+${errors.length - 1})` : ''}`);
      }
    } catch (err) {
      message.error(`${t('commerceProducts.batchCopyFailed')}: ${err.message}`);
    }
  };

  const startInlineEdit = (record) => {
    setInlineEditingId(record.id);
    setInlineDraft({
      name: record.name || '',
      description: record.description || '',
      linkUrl: record.linkUrl || '',
      brand: record.brand || '',
      customLabel0: record.customLabel0 || '',
      color: record.color || '',
      gender: record.gender || undefined,
      material: record.material || '',
      pattern: record.pattern || '',
      size: record.size || '',
      unitOfMeasure: record.unitOfMeasure || '',
      unitOfBrokenPack: record.unitOfBrokenPack || '',
      minOrderQuantity: record.minOrderQuantity ?? record.min_order_quantity ?? null,
      qtyPerUnit: record.qtyPerUnit ?? record.qty_per_unit ?? null,
      oqm: record.oqm ?? '',
      availability: record.availability || 'in stock',
      condition: record.condition || 'new',
      currency: record.currency || 'HKD',
      productionCategory: record.productionCategory || undefined,
      status: record.status || 'active',
      productType: record.productType || '',
      fbProductCategory: record.fbProductCategory || undefined
    });
  };

  const cancelInlineEdit = () => {
    setInlineEditingId(null);
    setInlineDraft({});
  };

  const updateInlineDraft = (field, value) => {
    setInlineDraft(prev => ({ ...prev, [field]: value }));
  };

  const saveInlineEdit = async (recordId) => {
    try {
      const payload = {
        name: (inlineDraft.name || '').trim(),
        description: inlineDraft.description || '',
        linkUrl: inlineDraft.linkUrl || '',
        brand: inlineDraft.brand || '',
        customLabel0: inlineDraft.customLabel0 || '',
        color: inlineDraft.color || '',
        gender: inlineDraft.gender || '',
        material: inlineDraft.material || '',
        pattern: inlineDraft.pattern || '',
        size: inlineDraft.size || '',
        unitOfMeasure: (inlineDraft.unitOfMeasure || '').trim(),
        unitOfBrokenPack: (inlineDraft.unitOfBrokenPack || '').trim(),
        minOrderQuantity: (() => {
          const v = inlineDraft.minOrderQuantity;
          if (v == null || v === '') return null;
          const n = Number(v);
          return Number.isFinite(n) && n > 0 ? Math.round(n * 10000) / 10000 : null;
        })(),
        qtyPerUnit: (() => {
          const v = inlineDraft.qtyPerUnit;
          if (v == null || v === '') return null;
          const n = Number(v);
          return Number.isFinite(n) && n > 0 ? Math.round(n * 10000) / 10000 : null;
        })(),
        oqm: (inlineDraft.oqm || '').trim() || null,
        availability: inlineDraft.availability || 'in stock',
        condition: inlineDraft.condition || 'new',
        currency: inlineDraft.currency || 'HKD',
        productionCategory: inlineDraft.productionCategory || null,
        status: inlineDraft.status || 'active',
        productType: inlineDraft.productType || '',
        fbProductCategory: inlineDraft.fbProductCategory || ''
      };
      if (!payload.name) {
        message.error(t('commerceProducts.nameRequired'));
        return;
      }
      const response = await fetchWithAuth(`/api/commerce/products/${recordId}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error(await extractResponseError(response));
      message.success(t('commerceProducts.saved'));
      cancelInlineEdit();
      await loadProducts();
    } catch (err) {
      message.error(`${t('commerceProducts.inlineSaveFailed')}: ${err.message}`);
    }
  };

  const syncDrawerPrimaryImage = (productId, mediaItems) => {
    const primaryImage = (Array.isArray(mediaItems) ? mediaItems : []).find(x => x.mediaType === 'image' && x.isPrimary)
      || (Array.isArray(mediaItems) ? mediaItems : []).find(x => x.mediaType === 'image');
    if (!primaryImage) return;
    const imageUrl = primaryImage.url || primaryImage.Url || '';
    if (!imageUrl) return;
    setSelectedProduct(prev => (prev?.id === productId ? { ...prev, imageUrl } : prev));
    setEditingRecord(prev => (prev?.id === productId ? { ...prev, imageUrl } : prev));
    form.setFieldsValue({ imageUrl });
  };

  const buildMediaUrl = (productId, path, companyId) => {
    const base = `/api/commerce/products/${productId}${path}`;
    if (!companyId) return base;
    return `${base}${path.includes('?') ? '&' : '?'}companyId=${encodeURIComponent(companyId)}`;
  };

  const loadProductMedia = async (productId, companyId) => {
    setMediaLoading(true);
    try {
      const response = await fetchWithAuth(buildMediaUrl(productId, '/media', companyId));
      if (!response.ok) throw new Error(await response.text());
      const data = await response.json();
      const items = Array.isArray(data) ? data : [];
      setProductMedia(items);
      const drafts = {};
      items.forEach(item => {
        drafts[item.id] = item.altText || '';
      });
      setAltDrafts(drafts);
      syncDrawerPrimaryImage(productId, items);
      return items;
    } catch (err) {
      message.error(`${t('commerceProducts.loadMediaFailed')}: ${err.message}`);
      return [];
    } finally {
      setMediaLoading(false);
    }
  };

  const openMediaManager = async (record) => {
    setSelectedProduct(record);
    setMediaModalOpen(true);
    setUploadTasks([]);
    await loadProductMedia(record.id, record.companyId);
  };

  const ensureProductRecordForMedia = async () => {
    if (editingRecord?.id) return editingRecord;
    const values = await validateProductFormValues();
    const payload = buildProductPayload(values);
    setMediaPreparing(true);
    try {
      const response = await fetchWithAuth('/api/commerce/products', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error(await extractResponseError(response));
      const created = await response.json();
      setEditingRecord(created);
      setSelectedProduct(created);
      message.success(t('commerceProducts.created'));
      await loadProducts();
      return created;
    } finally {
      setMediaPreparing(false);
    }
  };

  const openMediaManagerFromForm = async () => {
    try {
      const product = await ensureProductRecordForMedia();
      if (!product?.id) return;
      await openMediaManager(product);
    } catch (err) {
      if (err?.errorFields) return;
      message.error(`${t('commerceProducts.saveFailed')}: ${err.message}`);
    }
  };

  const validateMediaFile = (file) => {
    const isImage = file.type?.startsWith('image/');
    const isVideo = file.type?.startsWith('video/');
    if (!isImage && !isVideo) {
      message.error(t('commerceProducts.imageVideoOnly'));
      return Upload.LIST_IGNORE;
    }
    const sizeLimit = isImage ? 8 * 1024 * 1024 : 100 * 1024 * 1024;
    if (file.size > sizeLimit) {
      message.error(isImage ? t('commerceProducts.imageSizeLimit') : t('commerceProducts.videoSizeLimit'));
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const uploadMedia = async ({ file, onSuccess, onError, onProgress }) => {
    if (!selectedProduct) return;
    const taskId = file.uid || `${Date.now()}_${Math.random()}`;
    setUploadTasks(prev => [
      ...prev.filter(x => x.id !== taskId),
      { id: taskId, name: file.name, progress: 0, status: 'uploading', error: '', fileObj: file }
    ]);

    const updateTask = (patch) => {
      setUploadTasks(prev => prev.map(t => (t.id === taskId ? { ...t, ...patch } : t)));
    };

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mediaType', file.type?.startsWith('video/') ? 'video' : 'image');
      const xhr = new XMLHttpRequest();
      const token = localStorage.getItem('token');
      xhr.open('POST', buildMediaUrl(selectedProduct.id, '/media', selectedProduct.companyId));
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      const uploadResult = await new Promise((resolve, reject) => {
        xhr.upload.onprogress = (evt) => {
          if (!evt.lengthComputable) return;
          const percent = Math.min(99, Math.round((evt.loaded / evt.total) * 100));
          onProgress?.({ percent });
          updateTask({ progress: percent, status: 'uploading' });
        };
        xhr.onerror = () => reject(new Error('network error'));
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.responseText);
          else reject(new Error(xhr.responseText || `HTTP ${xhr.status}`));
        };
        xhr.send(formData);
      });

      updateTask({ progress: 100, status: 'done', error: '' });
      onSuccess?.({}, file);
      message.success(t('commerceProducts.mediaUploadSuccess'));
      const mediaItems = await loadProductMedia(selectedProduct.id, selectedProduct.companyId);
      syncDrawerPrimaryImage(selectedProduct.id, mediaItems);
      await loadProducts();
    } catch (err) {
      updateTask({ status: 'error', error: err.message, progress: 0 });
      onError?.(err);
      message.error(`${t('commerceProducts.uploadFailed')}: ${err.message}`);
    }
  };

  const retryUploadTask = async (task) => {
    if (!task?.fileObj) return;
    await uploadMedia({
      file: task.fileObj,
      onSuccess: () => {},
      onError: () => {},
      onProgress: () => {}
    });
  };

  const setPrimaryMedia = async (mediaId) => {
    if (!selectedProduct) return;
    try {
      const response = await fetchWithAuth(buildMediaUrl(selectedProduct.id, `/media/${mediaId}/set-primary`, selectedProduct.companyId), {
        method: 'POST'
      });
      if (!response.ok) throw new Error(await response.text());
      message.success(t('commerceProducts.setPrimary'));
      const mediaItems = await loadProductMedia(selectedProduct.id, selectedProduct.companyId);
      syncDrawerPrimaryImage(selectedProduct.id, mediaItems);
      await loadProducts();
    } catch (err) {
      message.error(`${t('commerceProducts.setPrimaryFailed')}: ${err.message}`);
    }
  };

  const saveAltText = async (mediaId) => {
    if (!selectedProduct) return;
    try {
      const response = await fetchWithAuth(buildMediaUrl(selectedProduct.id, `/media/${mediaId}`, selectedProduct.companyId), {
        method: 'PUT',
        body: JSON.stringify({ altText: altDrafts[mediaId] || '' })
      });
      if (!response.ok) throw new Error(await response.text());
      message.success(t('commerceProducts.altUpdated'));
      await loadProductMedia(selectedProduct.id, selectedProduct.companyId);
    } catch (err) {
      message.error(`${t('commerceProducts.altUpdateFailed')}: ${err.message}`);
    }
  };

  const deleteMedia = async (mediaId) => {
    if (!selectedProduct) return;
    try {
      const response = await fetchWithAuth(buildMediaUrl(selectedProduct.id, `/media/${mediaId}`, selectedProduct.companyId), {
        method: 'DELETE'
      });
      if (!response.ok) throw new Error(await response.text());
      message.success(t('commerceProducts.mediaDeleted'));
      const mediaItems = await loadProductMedia(selectedProduct.id, selectedProduct.companyId);
      syncDrawerPrimaryImage(selectedProduct.id, mediaItems);
      await loadProducts();
    } catch (err) {
      message.error(`${t('commerceProducts.deleteFailed')}: ${err.message}`);
    }
  };

  const reorderMedia = async (fromIndex, toIndex) => {
    if (!selectedProduct || toIndex < 0 || toIndex >= productMedia.length) return;
    const next = [...productMedia];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setProductMedia(next);
    try {
      const response = await fetchWithAuth(buildMediaUrl(selectedProduct.id, '/media/reorder', selectedProduct.companyId), {
        method: 'POST',
        body: JSON.stringify({ orderedMediaIds: next.map(x => x.id) })
      });
      if (!response.ok) throw new Error(await response.text());
    } catch (err) {
      message.error(`排序失敗: ${err.message}`);
      loadProductMedia(selectedProduct.id, selectedProduct.companyId);
    }
  };

  const onDragStartMedia = (id) => {
    setDraggingMediaId(id);
  };

  const onDropMedia = async (targetId) => {
    if (!draggingMediaId || draggingMediaId === targetId) return;
    const fromIndex = productMedia.findIndex(x => x.id === draggingMediaId);
    const toIndex = productMedia.findIndex(x => x.id === targetId);
    setDraggingMediaId(null);
    if (fromIndex < 0 || toIndex < 0) return;
    await reorderMedia(fromIndex, toIndex);
  };

  const columns = useMemo(() => [
    {
      title: t('commerceProducts.image'),
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      width: 88,
      render: (url) => (url ? <img src={url} alt="product" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4 }} /> : '-')
    },
    {
      title: t('commerceProducts.nameColUiLocale'),
      dataIndex: 'name',
      key: 'name',
      width: 220,
      ellipsis: true,
      render: (value, record) => (inlineEditingId === record.id
        ? <Input value={inlineDraft.name} onChange={(e) => updateInlineDraft('name', e.target.value)} />
        : (resolveLocalizedEntityName(value, record.nameLocales ?? record.name_locales, currentLanguage) || '—'))
    },
    {
      title: t('commerceProducts.description'),
      dataIndex: 'description',
      key: 'description',
      width: 240,
      ellipsis: true,
      render: (value, record) => (inlineEditingId === record.id
        ? <Input value={inlineDraft.description} onChange={(e) => updateInlineDraft('description', e.target.value)} />
        : (resolveLocalizedEntityName(value, record.descriptionLocales ?? record.description_locales, currentLanguage) || '—'))
    },
    {
      title: t('commerceProducts.link'),
      dataIndex: 'linkUrl',
      key: 'linkUrl',
      width: 220,
      ellipsis: true,
      render: (value, record) => (inlineEditingId === record.id
        ? <Input value={inlineDraft.linkUrl} onChange={(e) => updateInlineDraft('linkUrl', e.target.value)} />
        : (value || '-'))
    },
    { title: t('commerceProducts.company'), dataIndex: 'companyId', key: 'companyId', width: 190, render: (id) => (companiesForSelect.find(c => c.id === id)?.name) || id || '-' },
    { title: t('commerceProducts.contentId'), dataIndex: 'productRetailerId', key: 'productRetailerId', width: 160 },
    {
      title: t('commerceProducts.fbCategory'),
      dataIndex: 'fbProductCategory',
      key: 'fbProductCategory',
      width: 260,
      ellipsis: true,
      render: (value, record) => (inlineEditingId === record.id
        ? (
          <Select
            allowClear
            showSearch
            style={{ width: '100%' }}
            value={inlineDraft.fbProductCategory}
            onChange={(v) => updateInlineDraft('fbProductCategory', v)}
            options={fbCategoryOptions}
            loading={taxonomyLoading}
            notFoundContent={taxonomyLoading ? t('commerceCollections.loading') : t('commerceProducts.noCategory')}
            optionFilterProp="label"
            placeholder={t('commerceProducts.selectCategory')}
          />
        )
        : (value || '-'))
    },
    {
      title: t('commerceProducts.brand'),
      dataIndex: 'brand',
      key: 'brand',
      width: 160,
      render: (value, record) => (inlineEditingId === record.id
        ? <Input value={inlineDraft.brand} onChange={(e) => updateInlineDraft('brand', e.target.value)} />
        : (value || '-'))
    },
    {
      title: t('commerceProducts.contentNumber'),
      dataIndex: 'customLabel0',
      key: 'customLabel0',
      width: 150,
      render: (value, record) => (inlineEditingId === record.id
        ? <Input value={inlineDraft.customLabel0} onChange={(e) => updateInlineDraft('customLabel0', e.target.value)} />
        : (value || '-'))
    },
    {
      title: t('commerceProducts.color'),
      dataIndex: 'color',
      key: 'color',
      width: 120,
      render: (value, record) => (inlineEditingId === record.id
        ? <Input value={inlineDraft.color} onChange={(e) => updateInlineDraft('color', e.target.value)} />
        : (value || '-'))
    },
    {
      title: t('commerceProducts.gender'),
      dataIndex: 'gender',
      key: 'gender',
      width: 130,
      render: (value, record) => (inlineEditingId === record.id
        ? (
          <Select
            allowClear
            value={inlineDraft.gender}
            onChange={(v) => updateInlineDraft('gender', v)}
            options={GENDER_OPTIONS}
            style={{ width: '100%' }}
          />
        )
        : (value || '-'))
    },
    {
      title: '材質',
      dataIndex: 'material',
      key: 'material',
      width: 130,
      render: (value, record) => (inlineEditingId === record.id
        ? <Input value={inlineDraft.material} onChange={(e) => updateInlineDraft('material', e.target.value)} />
        : (value || '-'))
    },
    {
      title: t('commerceProducts.pattern'),
      dataIndex: 'pattern',
      key: 'pattern',
      width: 130,
      render: (value, record) => (inlineEditingId === record.id
        ? <Input value={inlineDraft.pattern} onChange={(e) => updateInlineDraft('pattern', e.target.value)} />
        : (value || '-'))
    },
    {
      title: t('commerceProducts.size'),
      dataIndex: 'size',
      key: 'size',
      width: 130,
      render: (value, record) => (inlineEditingId === record.id
        ? <Input value={inlineDraft.size} onChange={(e) => updateInlineDraft('size', e.target.value)} />
        : (value || '-'))
    },
    {
      title: t('commerceProducts.unitOfMeasure'),
      dataIndex: 'unitOfMeasure',
      key: 'unitOfMeasure',
      width: 100,
      render: (value, record) => (inlineEditingId === record.id
        ? <Input value={inlineDraft.unitOfMeasure} onChange={(e) => updateInlineDraft('unitOfMeasure', e.target.value)} maxLength={50} />
        : (resolveLocalizedEntityName(value, record.unitOfMeasureLocales ?? record.unit_of_measure_locales, currentLanguage) || '-'))
    },
    {
      title: t('commerceProducts.qtyPerUnit'),
      dataIndex: 'qtyPerUnit',
      key: 'qtyPerUnit',
      width: 120,
      render: (value, record) => (inlineEditingId === record.id
        ? (
          <InputNumber
            min={0}
            precision={4}
            step={0.0001}
            allowClear
            style={{ width: '100%' }}
            value={inlineDraft.qtyPerUnit}
            onChange={(v) => updateInlineDraft('qtyPerUnit', v)}
          />
        )
        : (value != null && value !== '' && Number.isFinite(Number(value)) && Number(value) > 0 ? Number(value) : '—'))
    },
    {
      title: t('commerceProducts.unitOfBrokenPack'),
      dataIndex: 'unitOfBrokenPack',
      key: 'unitOfBrokenPack',
      width: 100,
      render: (value, record) => (inlineEditingId === record.id
        ? <Input value={inlineDraft.unitOfBrokenPack} onChange={(e) => updateInlineDraft('unitOfBrokenPack', e.target.value)} maxLength={50} />
        : (resolveLocalizedEntityName(value, record.unitOfBrokenPackLocales ?? record.unit_of_brokenpack_locales, currentLanguage) || '-'))
    },
    {
      title: t('commerceProducts.moq'),
      dataIndex: 'minOrderQuantity',
      key: 'minOrderQuantity',
      width: 120,
      render: (value, record) => (inlineEditingId === record.id
        ? (
          <InputNumber
            min={0}
            precision={4}
            step={0.0001}
            allowClear
            style={{ width: '100%' }}
            value={inlineDraft.minOrderQuantity}
            onChange={(v) => updateInlineDraft('minOrderQuantity', v)}
          />
        )
        : (value != null && value !== '' && Number.isFinite(Number(value)) && Number(value) > 0 ? Number(value) : '—'))
    },
    {
      title: t('commerceProducts.oqm'),
      dataIndex: 'oqm',
      key: 'oqm',
      width: 140,
      ellipsis: true,
      render: (value, record) => (inlineEditingId === record.id
        ? <Input value={inlineDraft.oqm} onChange={(e) => updateInlineDraft('oqm', e.target.value)} maxLength={200} />
        : ((value ?? record.oqm) || '—'))
    },
    {
      title: '供應狀況',
      dataIndex: 'availability',
      key: 'availability',
      width: 140,
      render: (value, record) => (inlineEditingId === record.id
        ? <Select value={inlineDraft.availability} onChange={(v) => updateInlineDraft('availability', v)} options={AVAILABILITY_OPTIONS} style={{ width: '100%' }} />
        : (value || '-'))
    },
    {
      title: t('commerceProducts.condition'),
      dataIndex: 'condition',
      key: 'condition',
      width: 130,
      render: (value, record) => (inlineEditingId === record.id
        ? <Select value={inlineDraft.condition} onChange={(v) => updateInlineDraft('condition', v)} options={CONDITION_OPTIONS} style={{ width: '100%' }} />
        : (value || '-'))
    },
    {
      title: '貨幣',
      dataIndex: 'currency',
      key: 'currency',
      width: 110,
      render: (value, record) => (inlineEditingId === record.id
        ? <Input value={inlineDraft.currency} onChange={(e) => updateInlineDraft('currency', e.target.value)} />
        : (value || '-'))
    },
    {
      title: t('commerceProducts.effectivePriceCol'),
      key: 'effectivePrice',
      width: 140,
      render: (_, record) => {
        const v = record.effectivePrice != null ? record.effectivePrice : record.price;
        return v == null ? '—' : `${record.currency || 'HKD'} ${Number(v).toFixed(2)}`;
      }
    },
    {
      title: '生產分類',
      dataIndex: 'productionCategory',
      key: 'productionCategory',
      width: 140,
      render: (value, record) => (inlineEditingId === record.id
        ? <Input value={inlineDraft.productionCategory || ''} onChange={(e) => updateInlineDraft('productionCategory', e.target.value)} maxLength={50} />
        : (value || '-'))
    },
    {
      title: t('commerceProducts.status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (value, record) => (inlineEditingId === record.id
        ? <Select value={inlineDraft.status} onChange={(v) => updateInlineDraft('status', v)} options={STATUS_OPTIONS} style={{ width: '100%' }} />
        : <Tag color={String(value).toLowerCase() === 'active' ? 'green' : 'default'}>{value || 'unknown'}</Tag>)
    },
    ...buildCommerceAuditColumns(t, userTimezoneOffset),
    {
      title: t('commerceProducts.actions'),
      key: 'actions',
      width: 480,
      fixed: 'right',
      render: (_, record) => {
        const isEditing = inlineEditingId === record.id;
        return (
          <Space wrap={false} size={4}>
            {isEditing ? (
              <>
                <Button size="small" type="primary" onClick={() => saveInlineEdit(record.id)}>{t('commerceProducts.save')}</Button>
                <Button size="small" onClick={cancelInlineEdit}>{t('commerceProducts.cancel')}</Button>
              </>
            ) : (
              <Button size="small" onClick={() => startInlineEdit(record)}>{t('commerceProducts.inlineEdit')}</Button>
            )}
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>{t('commerceProducts.detail')}</Button>
            <Button size="small" icon={<PictureOutlined />} onClick={() => openMediaManager(record)}>{t('commerceProducts.media')}</Button>
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyProduct(record)}
            >
              {t('commerceProducts.copy')}
            </Button>
            <Button
              size="small"
              icon={<SyncOutlined spin={syncingId === record.id} />}
              onClick={() => syncProduct(record)}
              loading={syncingId === record.id}
            >
              {t('commerceProducts.uploadToMeta')}
            </Button>
            <Popconfirm title={t('commerceProducts.confirmDelete')} onConfirm={() => deleteProduct(record.id)}>
              <Button size="small" danger icon={<DeleteOutlined />}>{t('commerceProducts.delete')}</Button>
            </Popconfirm>
          </Space>
        );
      }
    }
  ], [inlineEditingId, inlineDraft, syncingId, fbCategoryOptions, taxonomyLoading, companiesForSelect, t, currentLanguage, userTimezoneOffset]);

  const selectionPhoneNumberRef = useMemo(() => {
    const selected = products.filter((p) => selectedRowKeys.includes(p.id));
    const uniq = [...new Set(selected.map((p) => (p.phoneNumberId || '').trim()))];
    if (uniq.length > 1) return 'mixed';
    return uniq[0] ?? '';
  }, [products, selectedRowKeys]);

  const collectionsEligibleForAdd = useMemo(() => {
    if (selectionPhoneNumberRef === 'mixed') return [];
    return collectionsForAdd.filter((c) => {
      const cp = (c.phoneNumberId || '').trim();
      if (!selectionPhoneNumberRef) return !cp;
      return cp === selectionPhoneNumberRef;
    });
  }, [collectionsForAdd, selectionPhoneNumberRef]);

  const filteredCollectionsForAddTable = useMemo(() => {
    const q = addToCollectionSearchKeyword.trim().toLowerCase();
    if (!q) return collectionsEligibleForAdd;
    return collectionsEligibleForAdd.filter((c) => {
      const companyLabel = (companiesForSelect.find((x) => x.id === c.companyId)?.name || '').toLowerCase();
      const locName = (
        resolveLocalizedEntityName(c.name, c.nameLocales ?? c.name_locales, currentLanguage) || ''
      ).toLowerCase();
      const hay = [c.name, c.description, c.externalCollectionKey, companyLabel, locName]
        .filter((x) => x != null && String(x).trim() !== '')
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [collectionsEligibleForAdd, addToCollectionSearchKeyword, companiesForSelect, currentLanguage]);

  const addToCollectionTableColumns = useMemo(
    () => [
      {
        title: t('commerceProducts.nameColUiLocale'),
        key: 'displayName',
        width: 220,
        ellipsis: true,
        render: (_, c) =>
          resolveLocalizedEntityName(c.name, c.nameLocales ?? c.name_locales, currentLanguage) || c.name || '—'
      },
      {
        title: t('commerceProducts.company'),
        dataIndex: 'companyId',
        key: 'companyId',
        width: 160,
        ellipsis: true,
        render: (id) => companiesForSelect.find((x) => x.id === id)?.name || id || '-'
      },
      {
        title: t('commerceProducts.description'),
        dataIndex: 'description',
        key: 'description',
        width: 200,
        ellipsis: true,
        render: (v) => v || '-'
      },
      {
        title: t('commerceCollections.externalCollectionKey'),
        dataIndex: 'externalCollectionKey',
        key: 'externalCollectionKey',
        width: 180,
        ellipsis: true,
        render: (v) => v || '-'
      }
    ],
    [t, currentLanguage, companiesForSelect]
  );

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space>
              <Button
                icon={<LinkOutlined />}
                onClick={openAddToCollectionModal}
                disabled={!selectedRowKeys || selectedRowKeys.length === 0}
              >
                {t('commerceProducts.addToCollection')} {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : ''}
              </Button>
              <Button
                icon={<SyncOutlined />}
                onClick={batchSyncProducts}
                disabled={!selectedRowKeys || selectedRowKeys.length === 0}
              >
                {t('commerceProducts.batchUploadToMeta')} {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : ''}
              </Button>
              <Button
                icon={<CopyOutlined />}
                onClick={batchCopyProducts}
                disabled={!selectedRowKeys || selectedRowKeys.length === 0}
              >
                {t('commerceProducts.batchCopy')} {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : ''}
              </Button>
              <Button icon={<ReloadOutlined />} onClick={loadProducts}>{t('commerceProducts.refresh')}</Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>{t('commerceProducts.addProduct')}</Button>
            </Space>
            <div style={{ textAlign: 'right' }}>
              <Title level={3} style={{ margin: 0 }}>{t('commerceProducts.title')}</Title>
              <Text type="secondary">{t('commerceProducts.description')}</Text>
            </div>
          </div>

          <Space wrap>
            <Input.Search
              placeholder={t('commerceProducts.searchKeywordPlaceholder')}
              allowClear
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onSearch={(v) => {
                setKeyword((v ?? '').trim());
                setSearchInput((v ?? '').trim());
                setListPage(1);
              }}
              style={{ width: 320 }}
              enterButton
            />
            <Select
              mode="multiple"
              allowClear
              showSearch
              maxTagCount="responsive"
              placeholder={t('commerceProducts.filterByCollectionsPlaceholder')}
              style={{ minWidth: 280, maxWidth: 420 }}
              value={collectionFilterIds}
              loading={collectionsForFilterLoading}
              options={collectionFilterOptions}
              optionFilterProp="label"
              onChange={(ids) => {
                setCollectionFilterIds(ids || []);
                setListPage(1);
              }}
              notFoundContent={collectionsForFilterLoading ? t('commerceCollections.loading') : t('commerceProducts.noCollectionFound')}
            />
            {isTenantAdmin && (
              <Select
                allowClear
                placeholder={t('commerceProducts.filterByCompany')}
                style={{ width: 260 }}
                value={companyFilter}
                onChange={(v) => {
                  setCompanyFilter(v);
                  setCollectionFilterIds([]);
                  setListPage(1);
                }}
                options={companiesForSelect.map(c => ({ label: c.name, value: c.id }))}
              />
            )}
            <Button loading={taxonomyLoading} onClick={() => syncTaxonomy('facebook')}>{t('commerceProducts.syncFacebookTaxonomy')}</Button>
          </Space>

          <Table
            rowKey="id"
            loading={loading}
            rowSelection={{
              selectedRowKeys,
              onChange: onProductTableSelectionChange
            }}
            columns={columns}
            dataSource={products}
            pagination={commerceServerTablePagination({
              page: listPage,
              pageSize: listPageSize,
              total: productsTotal,
              onChange: (p, ps) => {
                setListPage(p);
                setListPageSize(ps);
              }
            })}
            scroll={{ x: 3720, y: 'calc(100vh - 340px)' }}
          />
        </Space>
      </Card>

      <Drawer
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        width="95%"
        title={null}
        footer={(
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text strong style={{ fontSize: 16 }}>
              {t('commerceProducts.previewEffectiveAtServer')}:{' '}
              {drawerPreviewEffective != null && !Number.isNaN(Number(drawerPreviewEffective))
                ? `${drawerCurrency || 'HKD'} ${Number(drawerPreviewEffective).toFixed(2)}`
                : '—'}
            </Text>
            <Space>
              <Button onClick={() => setModalOpen(false)}>{t('commerceProducts.drawerClose')}</Button>
              <Button type="primary" loading={saving} onClick={saveProduct}>
                {t('commerceProducts.save')}
              </Button>
            </Space>
          </div>
        )}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={4} style={{ margin: 0 }}>
            {editingRecord ? t('commerceProducts.editProductModal') : t('commerceProducts.addProductModal')}
          </Title>
        </div>
        <Divider style={{ margin: '8px 0 16px' }} />
        <Form form={form} layout="vertical">
          <Row gutter={[16, 12]} align="bottom" style={{ marginBottom: 8 }}>
            <Col xs={24} lg={18}>
              <Form.Item
                name="name"
                label={<Text strong style={{ fontSize: 18 }}>{t('commerceProducts.productName')}</Text>}
                rules={[{ required: true, message: t('commerceCatalogAdmin.required') }]}
                style={{ marginBottom: 8 }}
              >
                <Input
                  size="large"
                  placeholder={t('commerceProducts.productName')}
                  style={{ fontSize: 20, fontWeight: 600, height: 50 }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={6}>
              <Form.Item style={{ marginBottom: 8 }}>
                <button
                  type="button"
                  onClick={openMediaManagerFromForm}
                  disabled={mediaPreparing}
                  title={mediaPreparing ? t('commerceProducts.creatingProductForMedia') : t('commerceProducts.primaryImage')}
                  style={{
                    width: '100%',
                    minHeight: 78,
                    border: 0,
                    background: 'transparent',
                    padding: 0,
                    cursor: mediaPreparing ? 'wait' : 'pointer',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center'
                  }}
                >
                  {drawerPrimaryImageUrl ? (
                    <img
                      src={drawerPrimaryImageUrl}
                      alt={t('commerceProducts.primaryImage')}
                      style={{ width: 70, height: 70, objectFit: 'cover', borderRadius: 6 }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 70,
                        height: 70,
                        borderRadius: 6,
                        border: '1px solid #d9d9d9',
                        background: '#fafafa',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        color: '#bfbfbf'
                      }}
                    >
                      <PictureOutlined style={{ fontSize: 34 }} />
                      <PlusOutlined
                        style={{
                          position: 'absolute',
                          right: 11,
                          bottom: 12,
                          fontSize: 18,
                          background: '#fafafa',
                          borderRadius: '50%'
                        }}
                      />
                    </div>
                  )}
                </button>
              </Form.Item>
            </Col>
          </Row>
          <Tabs
            activeKey={productFormTab}
            onChange={setProductFormTab}
            items={[
              {
                key: 'basic',
                label: t('commerceProducts.tabBasic'),
                children: (
                  <>
          <Row gutter={12}>
            <Col span={24}>
              <Text strong style={{ display: 'block', width: '100%', marginTop: 4 }}>
                {t('commerceProducts.formBasicSection')}
              </Text>
            </Col>
          </Row>
          <Row gutter={12}>
            {isTenantAdmin ? (
              <Col span={12}>
                <Form.Item name="companyId" label={t('commerceProducts.company')} rules={[{ required: true, message: t('commerceCatalogAdmin.required') }]}>
                  <Select
                    placeholder={t('commerceCollections.selectCompany')}
                    options={companiesForSelect.map(c => ({ label: c.name, value: c.id }))}
                    showSearch
                    optionFilterProp="label"
                    filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes((input || '').toLowerCase())}
                  />
                </Form.Item>
              </Col>
            ) : (
              <Col span={12}>
                <Form.Item label={t('commerceProducts.company')}>
                  <Input disabled value={companiesForSelect.find(c => c.id === userInfo.company_id)?.name || userInfo.company_id || '-'} />
                </Form.Item>
              </Col>
            )}
            <Col span={24}>
              <Text type="secondary">{t('commerceProducts.nameLocalesHint')}</Text>
            </Col>
            {COMMERCE_NAME_LOCALE_EXTRA_KEYS.map(({ jsonKey, formPath }) => (
              <Col span={12} key={jsonKey}>
                <Form.Item
                  name={formPath}
                  label={(
                    <span>
                      <Tooltip title={jsonKey}><Tag>{jsonKey === 'zh-TC' ? '繁' : '简'}</Tag></Tooltip>
                      {jsonKey === 'zh-TC' ? t('commerceProducts.nameLocaleZhTc') : t('commerceProducts.nameLocaleZhSc')}
                    </span>
                  )}
                >
                  <Input />
                </Form.Item>
              </Col>
            ))}
            <Col span={24}>
              <Form.Item name="description" label={t('commerceProducts.descriptionPrimary')}>
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Text type="secondary">{t('commerceProducts.descriptionLocalesHint')}</Text>
            </Col>
            {COMMERCE_DESCRIPTION_LOCALE_EXTRA_KEYS.map(({ jsonKey, formPath }) => (
              <Col span={12} key={`desc-${jsonKey}`}>
                <Form.Item
                  name={formPath}
                  label={(
                    <span>
                      <Tooltip title={jsonKey}><Tag>{jsonKey === 'zh-TC' ? '繁' : '简'}</Tag></Tooltip>
                      {jsonKey === 'zh-TC' ? t('commerceProducts.descriptionLocaleZhTc') : t('commerceProducts.descriptionLocaleZhSc')}
                    </span>
                  )}
                >
                  <Input.TextArea rows={2} />
                </Form.Item>
              </Col>
            ))}
            <Col span={12}>
              <Form.Item name="productRetailerId" label={t('commerceProducts.contentId')}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="metaProductId" label={t('commerceProducts.metaProductId')}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label={<Text strong>{t('commerceProducts.collectionsSection')}</Text>}
                tooltip={t('commerceProducts.productCollectionsTooltip')}
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  loading={collectionsForDrawerLoading}
                  value={productCollectionIds}
                  onChange={setProductCollectionIds}
                  optionFilterProp="label"
                  placeholder={t('commerceProducts.productCollectionsPlaceholder')}
                  options={collectionsForDrawer.map((c) => ({
                    label: resolveLocalizedEntityName(c.name, c.nameLocales ?? c.name_locales, currentLanguage) || c.name || c.id,
                    value: c.id
                  }))}
                  notFoundContent={collectionsForDrawerLoading ? t('commerceCollections.loading') : t('commerceProducts.noCollectionFound')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="metaCatalogId" label={t('commerceCollections.metaCatalogId')}>
                {catalogOptionsLoading ? (
                  <Input disabled placeholder={t('commerceCollections.loading')} />
                ) : catalogOptions.length === 1 ? (
                  <Input
                    disabled
                    value={`${catalogOptions[0].label} (${catalogOptions[0].value})`}
                    addonBefore={t('commerceProducts.linkedCatalogDefault')}
                  />
                ) : catalogOptions.length === 0 ? (
                  <Alert type="info" showIcon message={t('commerceProducts.noCatalogLinkedHint')} />
                ) : (
                  <Select
                    allowClear
                    showSearch
                    placeholder={t('commerceCollections.selectCatalog')}
                    options={catalogOptions}
                    notFoundContent={t('commerceCollections.noCatalog')}
                    optionFilterProp="label"
                    filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes((input || '').toLowerCase())}
                  />
                )}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="customLabel0" label={t('commerceProducts.customLabel0Label')}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="linkUrl" label={t('commerceProducts.link')}>
                <Input placeholder={t('commerceProducts.linkUrlPlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t('commerceProducts.media')}>
                <Card size="small" style={{ background: '#fafafa' }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Text type="secondary">{t('commerceProducts.mediaFormHint')}</Text>
                    <Button icon={<PictureOutlined />} onClick={openMediaManagerFromForm} loading={mediaPreparing}>
                      {t('commerceProducts.openMediaManager')}
                    </Button>
                  </Space>
                </Card>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12} style={{ marginTop: 8 }}>
            <Col span={24}>
              <Text strong style={{ display: 'block', width: '100%', marginTop: 4 }}>
                {t('commerceProducts.metaAttributes')}
              </Text>
            </Col>
          </Row>
          <Divider style={{ margin: '8px 0 16px' }} />
          <Alert type="info" showIcon style={{ marginBottom: 12 }} message={t('commerceProducts.metaRequiredHint')} />
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="brand" label={t('commerceProducts.brand')}>
                <Input maxLength={120} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="fbProductCategory" label={t('commerceProducts.fbCategory')}>
                <Select
                  allowClear
                  showSearch
                  options={fbCategoryOptions}
                  loading={taxonomyLoading}
                  notFoundContent={taxonomyLoading ? t('commerceCollections.loading') : t('commerceProducts.noCategory')}
                  optionFilterProp="label"
                  placeholder={t('commerceProducts.selectCategory')}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="productType" label={t('commerceProducts.productType')}>
                <Input maxLength={250} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="availability" label={t('commerceProducts.availability')} initialValue="in stock">
                <Select options={AVAILABILITY_OPTIONS} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="unitOfMeasure" label={t('commerceProducts.unitOfMeasure')}>
                <Input maxLength={50} placeholder={t('commerceProducts.unitOfMeasurePlaceholder')} />
              </Form.Item>
            </Col>
            {COMMERCE_UNIT_OF_MEASURE_LOCALE_EXTRA_KEYS.map(({ jsonKey, formPath }) => (
              <Col span={6} key={`uom-${jsonKey}`}>
                <Form.Item
                  name={formPath}
                  label={(
                    <span>
                      <Tooltip title={jsonKey}><Tag>{jsonKey === 'zh-TC' ? '繁' : '简'}</Tag></Tooltip>
                      {jsonKey === 'zh-TC' ? t('commerceProducts.unitOfMeasureLocaleZhTc') : t('commerceProducts.unitOfMeasureLocaleZhSc')}
                    </span>
                  )}
                >
                  <Input maxLength={50} />
                </Form.Item>
              </Col>
            ))}
            <Col span={6}>
              <Form.Item name="qtyPerUnit" label={t('commerceProducts.qtyPerUnit')} tooltip={t('commerceProducts.qtyPerUnitHint')}>
                <InputNumber min={0} precision={4} step={0.0001} allowClear style={{ width: '100%' }} placeholder="—" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="unitOfBrokenPack" label={t('commerceProducts.unitOfBrokenPack')} tooltip={t('commerceProducts.unitOfBrokenPackHint')}>
                <Input maxLength={50} placeholder={t('commerceProducts.unitOfBrokenPackPlaceholder')} />
              </Form.Item>
            </Col>
            {COMMERCE_UNIT_OF_BROKEN_PACK_LOCALE_EXTRA_KEYS.map(({ jsonKey, formPath }) => (
              <Col span={6} key={`ubp-${jsonKey}`}>
                <Form.Item
                  name={formPath}
                  label={(
                    <span>
                      <Tooltip title={jsonKey}><Tag>{jsonKey === 'zh-TC' ? '繁' : '简'}</Tag></Tooltip>
                      {jsonKey === 'zh-TC' ? t('commerceProducts.unitOfBrokenPackLocaleZhTc') : t('commerceProducts.unitOfBrokenPackLocaleZhSc')}
                    </span>
                  )}
                >
                  <Input maxLength={50} />
                </Form.Item>
              </Col>
            ))}
            <Col span={6}>
              <Form.Item name="minOrderQuantity" label={t('commerceProducts.moq')}>
                <InputNumber min={0} precision={4} step={0.0001} allowClear style={{ width: '100%' }} placeholder="—" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="oqm" label={t('commerceProducts.oqm')} tooltip={t('commerceProducts.oqmHint')}>
                <Input maxLength={200} placeholder={t('commerceProducts.oqmPlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="condition" label={t('commerceProducts.condition')} initialValue="new">
                <Select options={CONDITION_OPTIONS} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="color" label={t('commerceProducts.color')}>
                <Input maxLength={200} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="gender" label={t('commerceProducts.gender')}>
                <Select allowClear options={GENDER_OPTIONS} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="material" label={t('commerceProducts.material')}>
                <Input maxLength={200} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="pattern" label={t('commerceProducts.pattern')}>
                <Input maxLength={100} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="size" label={t('commerceProducts.size')}>
                <Input maxLength={200} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="productionCategory" label="生產分類">
                <Input maxLength={50} placeholder="生產分類" allowClear />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="status" label={t('commerceProducts.status')} initialValue="active">
                <Select options={STATUS_OPTIONS} />
              </Form.Item>
            </Col>
          </Row>

                  </>
                )
              },
              {
                key: 'pricing',
                label: t('commerceProducts.tabSellingPrice'),
                forceRender: true,
                children: (
                  <>
                    <Alert type="info" showIcon style={{ marginBottom: 12 }} message={t('commerceProducts.sellingPriceTabHint')} />
                    <Row gutter={12}>
                      <Col span={6}>
                        <Form.Item name="currency" label={t('commerceProducts.currency')} initialValue="HKD">
                          <Select options={[{ label: 'HKD', value: 'HKD' }, { label: 'USD', value: 'USD' }, { label: 'TWD', value: 'TWD' }, { label: 'CNY', value: 'CNY' }]} />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item name="price" label={t('commerceProducts.legacyPrice')}>
                          <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={12}>
                      <Col span={8}>
                        <Form.Item name="price1" label={t('commerceProducts.price1')}>
                          <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="validFrom1" label={t('commerceProducts.validFrom1')}>
                          <DatePicker showTime style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="validTo1" label={t('commerceProducts.validTo1')}>
                          <DatePicker showTime style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={12}>
                      <Col span={8}>
                        <Form.Item name="price2" label={t('commerceProducts.price2')}>
                          <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="validFrom2" label={t('commerceProducts.validFrom2')}>
                          <DatePicker showTime style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="validTo2" label={t('commerceProducts.validTo2')}>
                          <DatePicker showTime style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                )
              }
            ]}
          />
        </Form>
      </Drawer>

      <Modal
        open={addToCollectionModalOpen}
        title={t('commerceProducts.addToCollection')}
        onCancel={() => {
          setAddToCollectionModalOpen(false);
          setAddToCollectionSearchKeyword('');
          setSelectedCollectionIdsForAdd([]);
        }}
        onOk={saveAddToCollection}
        confirmLoading={savingAddToCollection}
        okText={t('commerceProducts.confirmAddToCollection')}
        cancelText={t('commerceProducts.cancel')}
        width="min(96vw, 1680px)"
        styles={{ body: { maxHeight: 'calc(100vh - 220px)', overflow: 'auto' } }}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <Text type="secondary">{t('commerceProducts.addToCollectionHint', { count: selectedRowKeys.length })}</Text>
          <Radio.Group
            value={collectionMode}
            onChange={(e) => {
              const v = e.target.value;
              setCollectionMode(v);
              if (v === 'existing') {
                setNewCollectionName('');
                setNewCollectionDescription('');
              } else {
                setSelectedCollectionIdsForAdd([]);
                setAddToCollectionSearchKeyword('');
              }
            }}
          >
            <Space direction="vertical">
              <Radio value="existing">{t('commerceProducts.addToExistingCollection')}</Radio>
              <Radio value="new">{t('commerceProducts.createNewCollection')}</Radio>
            </Space>
          </Radio.Group>
          {selectionPhoneNumberRef === 'mixed' && (
            <Alert type="warning" showIcon message={t('commerceProducts.addToCollectionMixedWaba')} />
          )}
          {collectionMode === 'existing' && selectionPhoneNumberRef !== 'mixed' && collectionsEligibleForAdd.length === 0 && !collectionsForAddLoading && (
            <Alert type="info" showIcon message={t('commerceProducts.addToCollectionNoCompatibleCollections')} />
          )}
          {collectionMode === 'existing' && selectionPhoneNumberRef !== 'mixed' && (
            <>
              <Row gutter={12}>
                <Col span={24}>
                  <Text strong style={{ display: 'block', width: '100%', marginTop: 4 }}>
                    {t('commerceProducts.selectCollection')}
                  </Text>
                </Col>
              </Row>
              <Input.Search
                allowClear
                placeholder={t('commerceProducts.addToCollectionSearchPlaceholder')}
                value={addToCollectionSearchKeyword}
                onChange={(e) => setAddToCollectionSearchKeyword(e.target.value)}
                style={{ marginBottom: 12 }}
                enterButton
              />
              <Text type="secondary">
                {t('commerceProducts.addToCollectionsSelectedCount').replace('{n}', String(selectedCollectionIdsForAdd.length))}
              </Text>
              <Table
                size="small"
                rowKey="id"
                loading={collectionsForAddLoading}
                columns={addToCollectionTableColumns}
                dataSource={filteredCollectionsForAddTable}
                rowSelection={{
                  selectedRowKeys: selectedCollectionIdsForAdd,
                  onChange: setSelectedCollectionIdsForAdd,
                  preserveSelectedRowKeys: true
                }}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: [10, 20, 50, 100],
                  showTotal: (total) => `${total}`
                }}
                scroll={{ x: 900, y: 440 }}
              />
            </>
          )}
          {collectionMode === 'new' && (
            <Row gutter={12}>
              <Col span={24}>
                <Text strong style={{ display: 'block', width: '100%', marginTop: 4 }}>
                  {t('commerceProducts.createNewCollection')}
                </Text>
              </Col>
              <Col span={24}>
                <Form.Item label={t('commerceCollections.collectionName')} required style={{ marginBottom: 8 }}>
                  <Input
                    placeholder={t('commerceProducts.newCollectionNamePlaceholder')}
                    value={newCollectionName}
                    onChange={(e) => setNewCollectionName(e.target.value)}
                    style={{ width: '100%', marginTop: 4 }}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label={t('commerceCollections.description')} style={{ marginBottom: 0 }}>
                  <Input.TextArea
                    rows={2}
                    placeholder={t('commerceProducts.newCollectionDescriptionPlaceholder')}
                    value={newCollectionDescription}
                    onChange={(e) => setNewCollectionDescription(e.target.value)}
                    style={{ width: '100%', marginTop: 4 }}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
        </Space>
      </Modal>

      <Modal
        open={mediaModalOpen}
        title={`${t('commerceProducts.mediaModalTitle')} - ${selectedProduct
          ? (resolveLocalizedEntityName(
              selectedProduct.name,
              selectedProduct.nameLocales ?? selectedProduct.name_locales,
              currentLanguage
            ) || selectedProduct.name || '')
          : ''}`}
        onCancel={() => setMediaModalOpen(false)}
        footer={null}
        width={980}
        destroyOnClose
        styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflow: 'auto' } }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <Text type="secondary">{t('commerceProducts.mediaModalHint')}</Text>
          <Dragger
            accept="image/*,video/*"
            multiple
            showUploadList={false}
            beforeUpload={validateMediaFile}
            customRequest={uploadMedia}
          >
            <p className="ant-upload-text">{t('commerceProducts.mediaUploadDragText')}</p>
            <p className="ant-upload-hint">{t('commerceProducts.mediaUploadHint')}</p>
          </Dragger>

          {uploadTasks.length > 0 && (
            <Card size="small" title={t('commerceProducts.batchUploadProgress')}>
              <Space direction="vertical" style={{ width: '100%' }}>
                {uploadTasks.map(task => (
                  <div key={task.id} style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <Text ellipsis style={{ maxWidth: 460 }}>{task.name}</Text>
                      <Space>
                        <Tag color={task.status === 'done' ? 'green' : task.status === 'error' ? 'red' : 'blue'}>{task.status}</Tag>
                        {task.status === 'error' && (
                          <Button size="small" onClick={() => retryUploadTask(task)}>{t('commerceProducts.retry')}</Button>
                        )}
                      </Space>
                    </div>
                    <Progress percent={task.progress || 0} size="small" status={task.status === 'error' ? 'exception' : undefined} />
                    {task.error ? <Text type="danger">{task.error}</Text> : null}
                  </div>
                ))}
              </Space>
            </Card>
          )}

          {mediaLoading ? (
            <Alert type="info" message={t('commerceProducts.loadingMedia')} />
          ) : productMedia.length === 0 ? (
            <Alert type="warning" message={t('commerceProducts.noMediaUploaded')} />
          ) : (
            <div style={{ display: 'grid', gap: 10 }}>
              {productMedia.map((item) => (
                <div
                  key={item.id}
                  draggable
                  onDragStart={() => onDragStartMedia(item.id)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDropMedia(item.id)}
                  style={{
                    border: '1px solid #e8e8e8',
                    borderRadius: 8,
                    padding: 10,
                    background: draggingMediaId === item.id ? '#fafafa' : '#fff',
                    cursor: 'move'
                  }}
                >
                  <Row gutter={12} align="middle">
                    <Col span={4}>
                      {item.mediaType === 'video' ? (
                        <video
                          src={item.url}
                          controls
                          muted
                          preload="metadata"
                          style={{ width: '100%', maxHeight: 90, borderRadius: 4, background: '#000' }}
                        />
                      ) : (
                        <img
                          src={item.url}
                          alt={item.altText || item.fileName || 'media'}
                          style={{ width: '100%', maxHeight: 90, objectFit: 'cover', borderRadius: 4 }}
                        />
                      )}
                    </Col>
                    <Col span={12}>
                      <Space wrap>
                        <Text strong>{item.fileName || item.filePath}</Text>
                        <Tag>{item.mediaType === 'video' ? t('commerceProducts.mediaTypeVideo') : t('commerceProducts.mediaTypeImage')}</Tag>
                        {item.isPrimary && <Tag color="gold">{t('commerceProducts.mediaTagPrimary')}</Tag>}
                      </Space>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <Text type="secondary" style={{ wordBreak: 'break-all' }}>{item.url}</Text>
                        <Button
                          type="text"
                          size="small"
                          icon={<CopyOutlined />}
                          title={t('commerceProducts.copyImageUrl')}
                          onClick={() => {
                            const path = (item.url || '').startsWith('http') ? item.url : `${window.location.origin}${(item.url || '').startsWith('/') ? item.url : '/' + item.url}`;
                            navigator.clipboard?.writeText(path).then(() => message.success(t('commerceProducts.copyImageUrlSuccess'))).catch(() => message.error(t('commerceProducts.copyFailed')));
                          }}
                        />
                      </div>
                      <Space.Compact style={{ width: '100%', marginTop: 6 }}>
                        <Input
                          value={altDrafts[item.id] ?? ''}
                          onChange={(e) => setAltDrafts(prev => ({ ...prev, [item.id]: e.target.value }))}
                          placeholder={t('commerceProducts.altTextPlaceholder')}
                        />
                        <Button onClick={() => saveAltText(item.id)}>{t('commerceProducts.saveAlt')}</Button>
                      </Space.Compact>
                    </Col>
                    <Col span={8}>
                      <Space style={{ float: 'right' }}>
                        {item.mediaType === 'image' && (
                          <Button
                            size="small"
                            type={item.isPrimary ? 'primary' : 'default'}
                            icon={item.isPrimary ? <StarFilled /> : <StarOutlined />}
                            onClick={() => setPrimaryMedia(item.id)}
                          >
                            {t('commerceProducts.primaryImage')}
                          </Button>
                        )}
                        <Popconfirm title={t('commerceProducts.confirmDeleteMedia')} onConfirm={() => deleteMedia(item.id)}>
                          <Button danger size="small" icon={<DeleteOutlined />}>{t('commerceProducts.delete')}</Button>
                        </Popconfirm>
                      </Space>
                    </Col>
                  </Row>
                </div>
              ))}
            </div>
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default CommerceProductsPage;

